import { AngleLeftIcon } from '@app/components/icons/angle-left';
import { AngleRightSmallIcon } from '@app/components/icons/angle-right-small';
import { Checkmark } from '@app/components/icons/checkmark';
import { FLAG_SPACES } from '@app/feature-flags/flags';
import { useFlags } from '@app/hooks/use-flags';
import {
  selectCurrentSpace,
  selectCurrentSpaceExternalId,
  selectOrgSpaces,
  selectOrgSpacesPageInfo,
  selectOrgSpacesStatus,
} from '@app/routes/organization/selectors';
import { useAppDispatch, useAppSelector } from '@app/store';
import { useTranslation } from '@hopin-team/provider-translation';
import React, { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import { ListLoadingSkelton } from './list-loading-skelton';

/**
 * Renders the space context inside the org switcher menu (EVE-16226).
 * Gated on the Spaces flag; hidden entirely when off. When on:
 *  - at most one space to pick from → a plain, non-interactive name;
 *  - more than one → a button that drills into the "Switch space" panel.
 */
export const SpaceSelectorRow = ({
  onOpenSwitcher,
}: {
  onOpenSwitcher: () => void;
}) => {
  const flags = useFlags();
  const spaces = useAppSelector(selectOrgSpaces);
  const status = useAppSelector(selectOrgSpacesStatus);
  const currentSpace = useAppSelector(selectCurrentSpace);
  const { t } = useTranslation();

  const spaceCount = spaces?.length ?? 0;

  // An in-flight or failed refresh leaves the list momentarily unavailable —
  // only a loaded, empty list means the user really has no spaces.
  if (!flags?.[FLAG_SPACES] || (spaceCount === 0 && status === 'success')) {
    return null;
  }

  // currentSpace.id is resolved on load; empty means no space selected
  if (!currentSpace.id) {
    return null;
  }

  // Nothing to switch between — either the org has a single space or the list
  // is unavailable after a failed refresh. Show the current space as plain text
  // instead of a drill-in that would open an empty panel.
  if (spaceCount <= 1) {
    return (
      <div
        className="flex items-center self-stretch min-h-[56px] mx-4 px-2 py-2.5 border border-[rgba(0,0,0,0.1)] rounded-[4px]"
        role="presentation"
      >
        <div className="flex-1 min-w-0">
          <div className="text-[12px] leading-[16px] text-gray-650">
            {t('orgSwitcher.spaces.label')}
          </div>
          <div className="text-[14px] leading-[20px] font-medium text-gray-900 truncate">
            {currentSpace.name}
          </div>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      className="flex items-center self-stretch min-h-[56px] mx-4 px-2 py-2.5 bg-white border border-[rgba(0,0,0,0.1)] rounded-[4px] text-left cursor-pointer hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      onClick={onOpenSwitcher}
      role="menuitem"
      aria-label={t('orgSwitcher.accessibility.openSpaceSwitcher', {
        spaceName: currentSpace.name,
      })}
    >
      <div className="flex-1 min-w-0">
        <div className="text-[12px] leading-[16px] text-gray-650">
          {t('orgSwitcher.spaces.label')}
        </div>
        <div className="text-[14px] leading-[20px] font-medium text-gray-900 truncate">
          {currentSpace.name}
        </div>
      </div>
      <AngleRightSmallIcon className="flex-shrink-0" aria-hidden="true" />
    </button>
  );
};

/**
 * The drill-in "Switch space" panel that replaces the org switcher menu body.
 * Cursor-based infinite scroll.
 */
export const SpaceSwitcherPanel = ({ onBack }: { onBack: () => void }) => {
  const dispatch = useAppDispatch();
  const spaces = useAppSelector(selectOrgSpaces);
  const currentSpaceId = useAppSelector(selectCurrentSpaceExternalId);
  const status = useAppSelector(selectOrgSpacesStatus);
  const pageInfo = useAppSelector(selectOrgSpacesPageInfo);
  const { t } = useTranslation();

  const { ref: loadingMoreRef, inView: loadingMoreInView } = useInView({
    rootMargin: '0px 0px 250px 0px',
    threshold: 0,
  });

  useEffect(() => {
    if (
      !loadingMoreInView ||
      !pageInfo.hasNextPage ||
      status === 'processing'
    ) {
      return;
    }
    dispatch({
      type: '[organization] fetch org spaces',
      payload: { loadMore: true },
    });
  }, [loadingMoreInView, pageInfo.hasNextPage, status, dispatch]);

  const onSelectSpace = (externalId: string, spaceName: string) => {
    dispatch({
      type: '[organization] org space changed',
      payload: {
        spaceExternalId: externalId,
        spaceName,
      },
    });
    onBack();
  };

  return (
    <div className="mt-2 flex flex-col flex-1 min-h-0 rounded-md bg-gray-100">
      <div className="flex items-center gap-2.5 px-2 py-3 border-b border-[#F0F1F4]">
        <button
          type="button"
          className="border-none bg-none cursor-pointer text-gray-650 text-base leading-none p-0 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          onClick={onBack}
          aria-label={t('orgSwitcher.accessibility.backToMenu')}
        >
          <AngleLeftIcon width={20} height={20} />
        </button>
        <span className="text-xs font-inherit text-gray-650 uppercase tracking-wider">
          {t('orgSwitcher.spaces.label')}
        </span>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-2 pb-3 pt-1.5">
        {spaces?.map(space => (
          <button
            key={space.externalId}
            type="button"
            className="flex items-center bg-white text-left rounded font-medium mb-2 p-2 cursor-pointer w-full hover:bg-blue-100 hover:text-primary min-h-[32px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            onClick={() => onSelectSpace(space.externalId, space.name)}
            role="menuitem"
            aria-label={t('orgSwitcher.accessibility.selectSpace', {
              spaceName: space.name,
            })}
          >
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900 max-w-[184px] truncate">
                {space.name}
              </div>
            </div>
            {space.externalId === currentSpaceId && (
              <Checkmark
                width={16}
                height={16}
                className="flex-shrink-0 text-primary"
                aria-hidden="true"
              />
            )}
          </button>
        ))}

        {status === 'processing' ? (
          <div
            role="status"
            aria-label={t('orgSwitcher.accessibility.loadingSpaces')}
          >
            <ListLoadingSkelton />
            <ListLoadingSkelton />
            <ListLoadingSkelton />
          </div>
        ) : null}

        {pageInfo.hasNextPage && status !== 'processing' && (
          <div className="h-4 w-full" ref={loadingMoreRef}></div>
        )}
      </div>
    </div>
  );
};
